<template>
    <div v-if="visible" ref="applicationOverlay" class="makeup-application-overlay" @click.self="closeDialog">
        <section ref="applicationDialog" tabindex="-1" class="makeup-application-dialog" role="dialog" aria-modal="true" aria-labelledby="makeupApplicationTitle">
            <header class="makeup-application-header">
                <div>
                    <p>INITIAL APPLICATION</p>
                    <h2 id="makeupApplicationTitle">申请成为化妆师</h2>
                    <span>申请将复用当前实名状态，无需重复上传身份证或学生证照片；其余材料仅用于本次审核。</span>
                </div>
                <button type="button" class="makeup-dialog-close" aria-label="关闭申请表" @click="closeDialog">×</button>
            </header>

            <form class="makeup-application-form" novalidate @submit.prevent="submitApplication">
                <main class="makeup-application-main">
                    <section class="makeup-form-section">
                        <div class="makeup-section-heading">
                            <span>01</span>
                            <div><h3>基础资料</h3><p>联系电话仅属于化妆师资料，验证码使用登录短信验证码。</p></div>
                        </div>
                        <div class="makeup-form-grid">
                            <role-portrait-upload :key="applicationEpoch" class="makeup-field-full" tone="makeup" v-model="form.personalPhoto" required @change="clearErrors" @error="setClientError($event, 'personalPhoto')" />
                            <label><span>化妆师联系电话 <b>*</b></span><input v-model.trim="form.phone" inputmode="numeric" maxlength="11" placeholder="请输入中国大陆手机号" /></label>
                            <label><span>短信验证码 <b>*</b></span><span class="makeup-code-input"><input v-model.trim="form.smsCode" inputmode="numeric" maxlength="6" placeholder="6 位验证码" /><button type="button" :disabled="smsSending || smsCooldown > 0" @click="requestSmsCode">{{ smsButtonText }}</button></span></label>
                            <label><span>省份 <b>*</b></span><div class="makeup-select"><button type="button" class="makeup-select-trigger" :class="{ 'is-placeholder': !form.provinceCode }" :aria-expanded="isSelectOpen('province')" aria-haspopup="listbox" @click="toggleSelect('province')">{{ selectedOptionLabel(provinceOptions, form.provinceCode, '请选择省份') }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen('province')" class="makeup-select-menu" role="listbox" aria-label="省份"><button v-for="item in provinceOptions" :key="item.value" type="button" role="option" :aria-selected="form.provinceCode === item.value" :class="{ active: form.provinceCode === item.value }" @click="selectProvince(item.value)">{{ item.label }}<i v-if="form.provinceCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                            <label><span>城市 <b>*</b></span><div class="makeup-select"><button type="button" class="makeup-select-trigger" :class="{ 'is-placeholder': !form.cityCode }" :disabled="!form.provinceCode" :aria-expanded="isSelectOpen('city')" aria-haspopup="listbox" @click="toggleSelect('city')">{{ selectedOptionLabel(cityOptions, form.cityCode, '请选择城市') }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen('city')" class="makeup-select-menu" role="listbox" aria-label="城市"><button v-for="item in cityOptions" :key="item.value" type="button" role="option" :aria-selected="form.cityCode === item.value" :class="{ active: form.cityCode === item.value }" @click="selectCity(item.value)">{{ item.label }}<i v-if="form.cityCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                            <label class="makeup-field-full"><span>区 / 县 <b>*</b></span><div class="makeup-select"><button type="button" class="makeup-select-trigger" :class="{ 'is-placeholder': !form.districtCode }" :disabled="!form.cityCode" :aria-expanded="isSelectOpen('district')" aria-haspopup="listbox" @click="toggleSelect('district')">{{ selectedOptionLabel(districtOptions, form.districtCode, '请选择区 / 县') }}<i aria-hidden="true">⌄</i></button><div v-if="isSelectOpen('district')" class="makeup-select-menu" role="listbox" aria-label="区或县"><button v-for="item in districtOptions" :key="item.value" type="button" role="option" :aria-selected="form.districtCode === item.value" :class="{ active: form.districtCode === item.value }" @click="selectDistrict(item.value)">{{ item.label }}<i v-if="form.districtCode === item.value" aria-hidden="true">✓</i></button></div></div></label>
                            <label class="makeup-field-full"><span>专业简介 <b>*</b></span><textarea v-model="form.professionalIntro" maxlength="2000" rows="4" placeholder="介绍妆发经验、服务范围与擅长的妆面风格。"></textarea><small>{{ professionalIntroLength }}/2000</small></label>
                        </div>
                    </section>



                    <section class="makeup-form-section">
                        <div class="makeup-section-heading">
                            <span>02</span>
                            <div><h3>原创化妆作品</h3><p>请一次上传恰好 5 张 JPG / PNG 原创作品，作品仅供后台整体审核。</p></div>
                        </div>
                        <professional-application-image-upload
                            label="原创作品"
                            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                            required
                            :max-count="5"
                            tone="makeup"
                            :files="form.originalWorks"
                            :preview-urls="form.originalWorkPreviewUrls"
                            hint="请逐张上传 JPG / PNG 图片；首次申请必须上传 5 张。"
                            @select="selectOriginalWorks($event)"
                            @remove="removeOriginalWork($event)" />
                    </section>

                    <section class="makeup-form-section">
                        <div class="makeup-section-heading makeup-section-heading--action">
                            <div class="makeup-section-number-copy"><span>03</span><div><h3>专业证书 <small>可选</small></h3><p>证书作为独立待审核项，不阻塞首次申请的整体审核。</p></div></div>
                            <button type="button" class="makeup-add-button" @click="addCertificate">新增证书</button>
                        </div>
                        <article v-for="(certificate, index) in form.certificates" :key="certificate.localId" class="makeup-repeat-card">
                            <button class="makeup-remove-circle" type="button" aria-label="移除证书" @click="removeCertificate(index)">×</button>
                            <label>证书名称<input v-model.trim="certificate.name" maxlength="255" placeholder="例如：专业化妆师资格证" /></label>
                            <professional-application-file-field
                                label="证书原件"
                                accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
                                hint="支持 JPG / PNG / PDF，单个最大 5 MiB"
                                tone="makeup"
                                :file="certificate.file"
                                @select="selectCertificateFile(certificate, $event)"
                                @remove="certificate.file = null; clearErrors()" />
                        </article>
                    </section>

                    <application-agreement-panel ref="agreementPanel" :state="agreement" :title="agreementTitle" :disabled="submitting"
                        @read="readAgreement" @toggle="toggleAgreement" @agree="acceptAgreement" @exit="exitAgreement" @retry="readAgreement" @back="returnToAgreement" />

                    <div v-if="formError || fieldErrors.length" class="makeup-form-error" role="alert">
                        <p v-if="formError">{{ formError }}</p>
                        <ul v-if="fieldErrors.length"><li v-for="(fieldError, index) in fieldErrors" :key="fieldError.field + '-' + index"><strong>{{ fieldLabel(fieldError.field) }}</strong>：{{ fieldError.message }}</li></ul>
                    </div>
                </main>

                <footer class="makeup-dialog-footer">
                    <span>提交前请确认材料真实、完整</span>
                    <div><button type="button" class="makeup-secondary-button" :disabled="submitting" @click="closeDialog">取消</button><button type="submit" class="makeup-submit-button" :disabled="submitting">{{ submitting ? '正在提交…' : '提交首次申请' }}</button></div>
                </footer>
            </form>
        </section>
    </div>
</template>

<script>
import qs from "qs";
import applicationAgreement from "./applicationAgreement";
import ApplicationAgreementPanel from "./ApplicationAgreementPanel";
import { regionData } from "element-china-area-data";
import request from "@/tool/request";
import API from "@/tool/api";
import developmentSmsCode from "@/tool/developmentSmsCode.cjs";
import RolePortraitUpload from "./RolePortraitUpload";
import ProfessionalApplicationFileField from "./ProfessionalApplicationFileField";
import ProfessionalApplicationImageUpload from "./ProfessionalApplicationImageUpload";
import {
    appendUploadFile,
    removeUploadFile,
} from "@/tool/professionalApplicationUpload.cjs";
import {
    getResultMessage,
    getResultPayload,
    isResultSuccess,
} from "@/tool/result";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png"];
let certificateSequence = 0;

const createCertificate = () => ({
    localId: "certificate-" + (certificateSequence += 1),
    name: "",
    file: null,
});

const asText = (value) => typeof value === "string" ? value.trim() : "";

export default {
    name: "MakeupArtistApplicationDialog",
    mixins: [applicationAgreement(2)],
    components: {
        ApplicationAgreementPanel,
        RolePortraitUpload,
        ProfessionalApplicationFileField,
        ProfessionalApplicationImageUpload,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            submitting: false,
            smsSending: false,
            smsCooldown: 0,
            smsTimer: null,
            formError: "",
            fieldErrors: [],
            openSelectId: null,
            form: this.createEmptyForm(),
        };
    },
    computed: {
        provinceOptions() {
            return Array.isArray(regionData) ? regionData : [];
        },
        selectedProvince() {
            return this.findOption(this.provinceOptions, this.form.provinceCode);
        },
        cityOptions() {
            return this.selectedProvince && Array.isArray(this.selectedProvince.children)
                ? this.selectedProvince.children
                : [];
        },
        selectedCity() {
            return this.findOption(this.cityOptions, this.form.cityCode);
        },
        districtOptions() {
            return this.selectedCity && Array.isArray(this.selectedCity.children)
                ? this.selectedCity.children
                : [];
        },
        selectedDistrict() {
            return this.findOption(this.districtOptions, this.form.districtCode);
        },
        professionalIntroLength() {
            return this.form.professionalIntro.length;
        },
        trimmedProfessionalIntro() {
            return this.form.professionalIntro.trim();
        },
        smsButtonText() {
            if (this.smsSending) return "发送中…";
            return this.smsCooldown > 0 ? this.smsCooldown + "s 后重试" : "获取验证码";
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(isVisible) {
                if (isVisible) this.openDialog();
            },
        },
    },
    mounted() {
        document.addEventListener("click", this.handleDocumentClick);
    },
    beforeDestroy() {
        this.stopSmsTimer();
        this.revokeOriginalWorkPreviews();
        document.removeEventListener("click", this.handleDocumentClick);
    },
    methods: {
        createEmptyForm() {
            return {
                phone: "",
                smsCode: "",
                provinceCode: "",
                cityCode: "",
                districtCode: "",
                professionalIntro: "",
                personalPhoto: null,
                originalWorks: [],
                originalWorkPreviewUrls: [],
                certificates: [],
            };
        },
        openDialog() {
            this.invalidateApplication();
            this.stopSmsTimer();
            this.revokeOriginalWorkPreviews();
            this.form = this.createEmptyForm();
            this.formError = "";
            this.fieldErrors = [];
            this.openSelectId = null;
        },
        closeDialog() {
            if (!this.submitting) { this.invalidateApplication(); this.$emit("close"); }
        },
        findOption(options, value) {
            return (options || []).find((item) => String(item.value) === String(value)) || null;
        },
        selectedOptionLabel(options, value, placeholder) {
            const option = this.findOption(options, value);
            return option ? option.label : placeholder;
        },
        isSelectOpen(selectId) {
            return this.openSelectId === selectId;
        },
        toggleSelect(selectId) {
            this.openSelectId = this.isSelectOpen(selectId) ? null : selectId;
        },
        selectProvince(value) {
            this.form.provinceCode = value;
            this.form.cityCode = "";
            this.form.districtCode = "";
            this.openSelectId = null;
        },
        selectCity(value) {
            this.form.cityCode = value;
            this.form.districtCode = "";
            this.openSelectId = null;
        },
        selectDistrict(value) {
            this.form.districtCode = value;
            this.openSelectId = null;
        },
        handleDocumentClick(event) {
            const target = event && event.target;
            if (!target || !target.closest || !target.closest(".makeup-select")) this.openSelectId = null;
        },
        setClientError(message, field = "") {
            this.formError = message;
            this.fieldErrors = field ? [{ field, message }] : [];
        },
        clearErrors() {
            this.formError = "";
            this.fieldErrors = [];
        },
        fieldLabel(field) {
            const labels = {
                phone: "联系电话",
                smsCode: "短信验证码",
                provinceCode: "服务地区",
                cityCode: "服务地区",
                districtCode: "服务地区",
                professionalIntro: "专业简介",
                personalPhoto: "本人真实照片",
                originalWorks: "原创作品",
                "certificates.file": "证书原件",
            };
            if (labels[field]) return labels[field];
            const certificateMatch = /^certificates\[(\d+)\]/.exec(field);
            if (certificateMatch) return `证书 ${Number(certificateMatch[1]) + 1}`;
            return "申请资料";
        },
        isImageFile(file) {
            return !!file && IMAGE_TYPES.indexOf(file.type) !== -1;
        },
        isPdfFile(file) {
            return !!file && file.type === "application/pdf";
        },
        validateSelectedFile(file, fileType) {
            if (!file) return "请选择文件";
            if (fileType === "image" && !this.isImageFile(file)) return "图片仅接受 JPG 或 PNG 格式";
            if (fileType === "certificate" && !this.isImageFile(file) && !this.isPdfFile(file)) return "证书仅接受 JPG、PNG 或 PDF 格式";
            if (file.size > MAX_FILE_SIZE) return "单个文件不能超过 5 MiB";
            return "";
        },
        readFiles(event) {
            const files = Array.prototype.slice.call((event.target && event.target.files) || []);
            if (event.target) event.target.value = "";
            return files;
        },
        selectSingleFile(field, event, fileType) {
            const file = this.readFiles(event)[0];
            const error = this.validateSelectedFile(file, fileType);
            if (error) {
                this.setClientError(error, field);
                return;
            }
            this.form[field] = file;
            this.clearErrors();
        },
        removeSingleFile(field) {
            this.form[field] = null;
            this.clearErrors();
        },
        selectOriginalWorks(event) {
            const file = this.readFiles(event)[0];
            if (!file) return;
            if (this.form.originalWorks.length >= 5) {
                this.setClientError("原创作品最多只能上传 5 张", "originalWorks");
                return;
            }
            const error = this.validateSelectedFile(file, "image");
            if (error) {
                this.setClientError(error, "originalWorks");
                return;
            }
            this.form.originalWorks = appendUploadFile(this.form.originalWorks, file, 5);
            this.form.originalWorkPreviewUrls.push(URL.createObjectURL(file));
            this.clearErrors();
        },
        removeOriginalWork(index) {
            URL.revokeObjectURL(this.form.originalWorkPreviewUrls[index]);
            this.form.originalWorkPreviewUrls.splice(index, 1);
            this.form.originalWorks = removeUploadFile(this.form.originalWorks, index);
            this.clearErrors();
        },
        revokeOriginalWorkPreviews() {
            (this.form.originalWorkPreviewUrls || []).forEach((url) => URL.revokeObjectURL(url));
        },
        addCertificate() {
            this.form.certificates.push(createCertificate());
        },
        removeCertificate(index) {
            this.form.certificates.splice(index, 1);
        },
        selectCertificateFile(certificate, event) {
            const file = this.readFiles(event)[0];
            const error = this.validateSelectedFile(file, "certificate");
            if (error) {
                this.setClientError(error, "certificates.file");
                return;
            }
            certificate.file = file;
            this.clearErrors();
        },
        requestSmsCode() {
            const context = this.applicationContext();
            if (!/^1[3-9]\d{9}$/.test(this.form.phone)) {
                this.setClientError("请先填写有效的中国大陆手机号", "phone");
                return;
            }
            if (this.smsSending || this.smsCooldown > 0) return;
            this.smsSending = true;
            this.clearErrors();
            return request.post(API.creatorCenter.smsCode, qs.stringify({ phone: this.form.phone, professionalType: 2 })).then((response) => {
                if (!this.applicationIsCurrent(context)) return;
                if (!isResultSuccess(response)) throw response;
                const smsCode = developmentSmsCode.extractDevelopmentSmsCode(response.data);
                if (smsCode) this.form.smsCode = smsCode;
                this.smsCooldown = 60;
                this.smsTimer = window.setInterval(() => {
                    this.smsCooldown -= 1;
                    if (this.smsCooldown <= 0) this.stopSmsTimer();
                }, 1000);
                this.$message.success(getResultMessage(response, "验证码已发送"));
            }).catch((error) => {
                if (this.applicationIsCurrent(context)) this.setClientError(getResultMessage(error, "验证码发送失败，请稍后重试"), "smsCode");
            }).finally(() => {
                if (this.applicationIsCurrent(context)) this.smsSending = false;
            });
        },
        stopSmsTimer() {
            if (this.smsTimer) window.clearInterval(this.smsTimer);
            this.smsTimer = null;
            this.smsCooldown = 0;
        },
        getFilledCertificates() {
            return this.form.certificates.filter((item) => item.name || item.file);
        },
        validateForm() {
            const errors = [];
            const regionSelected = this.selectedProvince && this.selectedCity && this.selectedDistrict;
            if (!/^1[3-9]\d{9}$/.test(this.form.phone)) errors.push({ field: "phone", message: "请填写有效的中国大陆手机号" });
            if (!/^\d{6}$/.test(this.form.smsCode)) errors.push({ field: "smsCode", message: "请填写 6 位登录短信验证码" });
            if (!regionSelected) errors.push({ field: "provinceCode", message: "请选择完整的省、市、区／县" });
            if (this.trimmedProfessionalIntro.length < 1 || this.trimmedProfessionalIntro.length > 2000) errors.push({ field: "professionalIntro", message: "专业简介长度须为 1–2000 字" });
            if (!this.form.personalPhoto) errors.push({ field: "personalPhoto", message: "请上传本人真实照片" });
            if (this.form.originalWorks.length !== 5) errors.push({ field: "originalWorks", message: "必须恰好上传 5 张原创作品" });
            this.getFilledCertificates().forEach((certificate, index) => {
                if (!certificate.name || !certificate.file) errors.push({ field: "certificates[" + index + "]", message: "填写证书时需同时提供名称和原件" });
            });
            this.fieldErrors = errors;
            this.formError = errors.length ? "请完善以下内容后再提交" : "";
            return errors.length === 0;
        },
        buildFormData() {
            const formData = new FormData();
            formData.append("phone", this.form.phone);
            formData.append("smsCode", this.form.smsCode);
            formData.append("provinceCode", this.selectedProvince.value);
            formData.append("cityCode", this.selectedCity.value);
            formData.append("districtCode", this.selectedDistrict.value);
            formData.append("provinceName", this.selectedProvince.label);
            formData.append("cityName", this.selectedCity.label);
            formData.append("districtName", this.selectedDistrict.label);
            formData.append("professionalIntro", this.trimmedProfessionalIntro);
            formData.append("personalPhoto", this.form.personalPhoto);
            this.appendApplicationAgreement(formData);
            this.form.originalWorks.forEach((file) => formData.append("originalWorks", file));
            this.getFilledCertificates().forEach((certificate, index) => {
                formData.append("certificates[" + index + "].name", certificate.name);
                formData.append("certificates[" + index + "].file", certificate.file);
            });
            return formData;
        },
        errorPayload(error) {
            if (error && error.response && error.response.data) return error.response.data;
            if (error && error.data) return error.data;
            return getResultPayload(error);
        },
        applySubmitError(error) {
            const payload = this.errorPayload(error) || {};
            const data = payload.data || {};
            this.fieldErrors = Array.isArray(data.fieldErrors) ? data.fieldErrors.map((item) => ({
                field: asText(item.field) || "表单字段",
                message: asText(item.message) || "字段校验失败",
            })) : [];
            this.formError = asText(payload.message) || getResultMessage(error, "申请提交失败，请稍后重试");
        },
        submitApplication() {
            if (this.submitting || !this.checkApplicationAgreement()) return;
            const context = this.applicationContext();
            if (this.submitting || !this.validateForm()) return;
            this.submitting = true;
            this.clearErrors();
            return request.post(API.creatorCenter.makeupArtist.applications, this.buildFormData())
                .then((response) => {
                    if (!this.applicationIsCurrent(context)) return;
                    if (!isResultSuccess(response)) throw response;
                    this.$message.success(getResultMessage(response, "化妆师申请已提交，等待审核"));
                    this.$emit("submitted");
                    this.$emit("close");
                })
                .catch((error) => {
                    if (!this.applicationIsCurrent(context)) return;
                    this.applySubmitError(error);
                    this.recoverAgreementConflict(error);
                })
                .finally(() => {
                    if (this.applicationIsCurrent(context)) this.submitting = false;
                });
        },
    },
};
</script>

<style scoped>
.makeup-application-overlay { position: fixed; z-index: 60; inset: 0; display: grid; place-items: center; padding: 28px; background: rgba(32, 27, 41, .56); overscroll-behavior: none; touch-action: pan-y pinch-zoom; }
.makeup-application-dialog { display: flex; width: min(920px, calc(100vw - 56px)); max-height: min(860px, calc(100vh - 56px)); flex-direction: column; overflow: hidden; color: #332d49; background: #fff; border: 1px solid #f1d7df; border-radius: 22px; box-shadow: 0 28px 90px rgba(73, 35, 54, .28); }
.makeup-application-header { display: flex; flex: 0 0 auto; align-items: flex-start; justify-content: space-between; gap: 24px; padding: 22px 28px 18px; border-bottom: 1px solid #f4e5e9; }
.makeup-application-header p { margin: 0 0 6px; color: #c56782; font-size: 10px; font-weight: 800; letter-spacing: .13em; }.makeup-application-header h2 { margin: 0; color: #3f3158; font-size: 26px; font-weight: 800; }.makeup-application-header span { display: block; margin-top: 6px; color: #7c7483; font-size: 12px; }
.makeup-dialog-close { display: grid; width: 34px; height: 34px; flex: 0 0 auto; place-items: center; color: #765a68; background: #fff1f4; border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 23px; }
.makeup-application-form { display: flex; min-height: 0; flex: 1; flex-direction: column; }.makeup-application-main { min-height: 0; padding: 24px 28px; overflow-y: auto; overscroll-behavior: contain; }
.makeup-form-section { padding: 0 0 24px; margin-bottom: 24px; border-bottom: 1px solid #f2edf0; }.makeup-form-section:last-of-type { padding-bottom: 0; margin-bottom: 0; border-bottom: 0; }.makeup-section-heading { display: flex; gap: 11px; align-items: flex-start; margin-bottom: 16px; }.makeup-section-heading > span, .makeup-section-number-copy > span { display: grid; width: 26px; height: 26px; flex: 0 0 auto; place-items: center; color: #b15571; background: #fff0f4; border-radius: 8px; font-size: 10px; font-weight: 900; }.makeup-section-heading h3 { margin: 1px 0 2px; color: #4a4156; font-size: 15px; }.makeup-section-heading h3 small { color: #938a98; font-size: 11px; font-weight: 500; }.makeup-section-heading p { margin: 0; color: #897f8d; font-size: 11px; line-height: 1.6; }.makeup-section-heading--action { justify-content: space-between; }.makeup-section-number-copy { display: flex; gap: 11px; }
.makeup-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 13px; }.makeup-form-grid > label, .makeup-repeat-card > label { display: grid; gap: 6px; color: #5c5563; font-size: 12px; font-weight: 700; }.makeup-form-grid input, .makeup-form-grid textarea, .makeup-repeat-card input { width: 100%; padding: 9px 10px; color: #332d49; background: #fff; border: 1px solid #dfd5da; border-radius: 8px; font: inherit; font-size: 12px; }.makeup-form-grid textarea { min-height: 82px; resize: vertical; }.makeup-form-grid small { margin-top: -3px; color: #9b939f; font-size: 10px; font-weight: 500; text-align: right; }.makeup-field-full { grid-column: 1 / -1; }.makeup-form-grid b, .makeup-attachment-field b, .makeup-works-heading b { color: #dc657f; }
.makeup-select { position: relative; }.makeup-select-trigger { display: flex; width: 100%; min-height: 38px; align-items: center; justify-content: space-between; gap: 10px; padding: 9px 11px; color: #332d49; background: #fff; border: 1px solid #e7bdc9; border-radius: 11px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 400; text-align: left; }.makeup-select-trigger:hover, .makeup-select-trigger[aria-expanded="true"] { border-color: #d9849b; }.makeup-select-trigger:disabled { cursor: not-allowed; color: #aaa3ae; background: #f5f5f7; border-color: #d9d5dc; }.makeup-select-trigger i { color: #bf6e84; font-size: 16px; font-style: normal; line-height: 1; }.makeup-select-menu { position: absolute; z-index: 6; top: calc(100% + 7px); right: 0; left: 0; max-height: 216px; padding: 6px; overflow-y: auto; overscroll-behavior: contain; background: #fff; border: 1px solid #e9bdca; border-radius: 13px; box-shadow: 0 12px 24px rgba(100, 59, 77, .1); }.makeup-select-menu button { display: flex; width: 100%; min-height: 34px; align-items: center; justify-content: space-between; gap: 8px; padding: 7px 9px; color: #4e5962; background: transparent; border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-size: 12px; text-align: left; }.makeup-select-menu button:hover { color: #a44f68; background: #fff0f4; }.makeup-select-menu button.active { color: #8d3e56; background: #ffe6ed; }.makeup-select-menu button i { color: #c55d78; font-style: normal; }
.makeup-code-input { display: flex; gap: 6px; }.makeup-code-input button { flex: 0 0 auto; padding: 0 9px; color: #a54f68; background: #fff1f4; border: 1px solid #f0c7d2; border-radius: 8px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.makeup-code-input button:disabled { cursor: not-allowed; opacity: .6; }
.makeup-attachment-grid { display: grid; grid-template-columns: minmax(0, 360px); gap: 10px; }.makeup-attachment-field { display: grid; gap: 5px; min-height: 76px; padding: 10px; color: #5e5763; background: #fffafb; border: 1px dashed #e7b8c6; border-radius: 10px; cursor: pointer; }.makeup-attachment-field span { font-size: 11px; font-weight: 800; }.makeup-attachment-field strong { overflow: hidden; color: #a85b73; font-size: 10px; font-weight: 600; line-height: 1.5; text-overflow: ellipsis; white-space: nowrap; }.makeup-attachment-field em { color: #9a929d; font-size: 10px; font-style: normal; }.makeup-attachment-field input, .makeup-works-upload input { display: none; }.makeup-agreement-copy { margin: 11px 0 0; color: #7e7682; font-size: 11px; line-height: 1.6; }.makeup-agreement-copy a { margin: 0 2px; color: #b3536e; font-weight: 700; text-decoration: none; }.makeup-agreement-copy a:hover { text-decoration: underline; }
.makeup-works-heading { display: flex; justify-content: space-between; color: #5c5563; font-size: 11px; }.makeup-works-heading span { color: #ad7a36; }.makeup-works-heading span.valid { color: #438262; }.makeup-works-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }.makeup-work-preview { position: relative; display: grid; width: 92px; gap: 4px; overflow: hidden; }.makeup-work-preview img { display: block; width: 92px; height: 72px; object-fit: cover; border-radius: 7px; }.makeup-work-preview span { overflow: hidden; color: #9a5d70; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }.makeup-work-preview button { position: absolute; top: 3px; right: 3px; display: grid; width: 20px; height: 20px; place-items: center; padding: 0; color: #fff; background: rgba(73, 48, 61, .72); border: 0; border-radius: 50%; cursor: pointer; font: inherit; font-size: 16px; line-height: 1; }.makeup-works-upload { max-width: 100%; padding: 5px 7px; overflow: hidden; color: #a74f69; background: #fff; border: 1px dashed #e4aab9; border-radius: 6px; cursor: pointer; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.makeup-add-button { padding: 6px 9px; color: #a24e68; background: #fff; border: 1px solid #e7b6c5; border-radius: 8px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.makeup-add-button:hover { color: #fff; background: #d96887; border-color: #d96887; }.makeup-repeat-card { position: relative; display: grid; gap: 9px; margin-top: 12px; padding: 13px; background: #fffafb; border: 1px solid #f0e1e6; border-radius: 12px; }.makeup-repeat-card .makeup-attachment-field { min-height: 61px; }.makeup-remove-circle { position: absolute; top: 7px; right: 7px; display: grid; width: 20px; height: 20px; place-items: center; color: #966978; background: #f9eaf0; border: 0; border-radius: 50%; cursor: pointer; font: inherit; }
.makeup-form-error { margin: 0; padding: 10px 12px; color: #a1495c; background: #fff1f3; border-radius: 8px; font-size: 12px; line-height: 1.6; }.makeup-form-error p { margin: 0; font-weight: 700; }.makeup-form-error ul { margin: 6px 0 0; padding-left: 18px; }.makeup-form-error strong { font-family: ui-monospace, SFMono-Regular, Consolas, monospace; font-size: 11px; }
.makeup-dialog-footer { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 16px; padding: 15px 28px; color: #8a818d; background: #fff; border-top: 1px solid #f4e5e9; font-size: 11px; }.makeup-dialog-footer > span { max-width: 56%; line-height: 1.6; }.makeup-dialog-footer > div { display: flex; gap: 10px; }.makeup-secondary-button, .makeup-submit-button { min-height: 38px; padding: 8px 14px; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.makeup-secondary-button { color: #6b6370; background: #fff; border: 1px solid #d7d2d9; }.makeup-submit-button { color: #fff; background: #d56886; border: 1px solid #d56886; }.makeup-submit-button:disabled, .makeup-secondary-button:disabled { cursor: not-allowed; opacity: .6; }
.makeup-form-grid input::placeholder,
.makeup-form-grid textarea::placeholder,
.makeup-repeat-card input::placeholder {
    color: #a8a3ad;
    font-weight: 400;
    opacity: 1;
}
.makeup-form-grid input,
.makeup-form-grid textarea,
.makeup-repeat-card input {
    font-weight: 400;
}
.makeup-form-grid input:focus,
.makeup-form-grid textarea:focus,
.makeup-repeat-card input:focus {
    border-color: #dfd5da;
    outline: 0;
    box-shadow: none;
}
.makeup-select-trigger.is-placeholder {
    color: #a8a3ad;
    font-weight: 400;
}
.makeup-application-dialog button:focus-visible { outline: 0 !important; box-shadow: none !important; }
.makeup-dialog-close { line-height: 1 !important; text-align: center; }
@media (max-width: 760px) { .makeup-application-overlay { padding: 12px; }.makeup-application-dialog { width: 100%; max-height: calc(100vh - 24px); border-radius: 16px; }.makeup-application-header, .makeup-application-main, .makeup-dialog-footer { padding-right: 16px; padding-left: 16px; }.makeup-attachment-grid, .makeup-form-grid { grid-template-columns: 1fr; }.makeup-field-full { grid-column: auto; }.makeup-dialog-footer { align-items: flex-start; flex-direction: column; }.makeup-dialog-footer > span { max-width: none; } }
.makeup-works-upload-panel input { display: none; }
.makeup-works-upload-panel { display: block; margin-top: 8px; padding: 12px; background: #fffafb; border: 1px dashed #e7b8c6; border-radius: 11px; cursor: pointer; }
.makeup-works-upload-panel.is-complete { cursor: default; }
.makeup-works-upload-box { display: flex; min-height: 98px; align-items: center; justify-content: center; gap: 10px; padding: 12px; background: #fff; border: 1px dashed #e7b8c6; border-radius: 10px; text-align: center; }
.makeup-works-upload-box > i { display: grid; width: 30px; height: 30px; flex: 0 0 auto; place-items: center; color: #c55d78; background: #fff; border: 1px solid #e7b8c6; border-radius: 8px; font-size: 22px; font-style: normal; line-height: 1; }
.makeup-works-upload-box > span { display: grid; gap: 2px; }
.makeup-works-upload-box strong { color: #b15571; font-size: 13px; }
.makeup-works-upload-box strong.valid { color: #438262; }
.makeup-works-upload-box em { color: #857f76; font-size: 11px; font-style: normal; font-weight: 400; line-height: 1.6; }

/* #88: reuse the application form, adapting the supplied phone design to desktop. */
.makeup-application-dialog { color: #1c1c1e; border-color: #efedf5; border-radius: 24px; }
.makeup-application-header p { color: #7c6ff0; font-size: 10px; letter-spacing: .16em; }
.makeup-application-header h2 { color: #1c1c1e; font-size: 24px; }
.makeup-application-header { border-bottom-color: #efedf5; }
.makeup-section-heading h3 { color: #1c1c1e; }
.makeup-section-heading > span, .makeup-section-number-copy > span { display: none; }
.makeup-submit-button { background: #7c6ff0; border-color: #7c6ff0; min-height: 48px; border-radius: 12px; }
.makeup-dialog-close, .makeup-add-button { color: #7c6ff0; background: #fff; border-color: #ded9ff; }
.makeup-application-dialog button:focus-visible, .makeup-application-dialog input:focus-visible, .makeup-application-dialog textarea:focus-visible { outline: 3px solid #5444cc !important; outline-offset: 2px; }
@media (max-width:760px) {
 .makeup-application-header h2 { font-size: 20px; }
 .makeup-application-main { padding: 24px 16px; }
 .makeup-form-grid input, .makeup-form-grid textarea { font-size: 14px; min-height: 44px; }
 .makeup-dialog-footer > span { display: none; }
 .makeup-dialog-footer > div { width: 100%; }
 .makeup-submit-button { flex: 1; }
}

.makeup-application-dialog ::v-deep .role-portrait-upload__box,
.makeup-application-dialog ::v-deep .professional-image-upload__tile--add,
.makeup-application-dialog ::v-deep .professional-application-file-field__picker { color: #7c6ff0; background: #f6f4ff; border-color: #d1caff; }
.makeup-application-dialog ::v-deep .role-portrait-upload__placeholder i,
.makeup-application-dialog ::v-deep .professional-image-upload__add-content i { width: 26px; height: 26px; color: transparent; font-size: 0; border: 0; border-radius: 0; background: url('../../assets/images/application/bfd8e.svg') center no-repeat; }
.makeup-application-dialog ::v-deep .role-portrait-upload__placeholder strong,
.makeup-application-dialog ::v-deep .professional-image-upload__add-content strong { color: #7c6ff0; }
.makeup-add-button { display: inline-flex; align-items: center; gap: 4px; min-height: 36px; }
.makeup-add-button::before { content: ''; width: 12px; height: 12px; background: url('../../assets/images/application/90dc9.svg') center no-repeat; }
.makeup-code-input button { color: #6254cf; border-color: #ded9ff; background: #f1eeff; }
.makeup-select-trigger { border-color: #e6e4ee; }
.makeup-select-trigger > i { width: 15px; height: 15px; color: transparent; font-size: 0; background: url('../../assets/images/application/dd1a5.svg') center no-repeat; }

.makeup-section-heading h3 { font-weight: 700; }
.makeup-add-button { flex-shrink: 0; white-space: nowrap; }
@media(max-width:760px) { .makeup-application-dialog ::v-deep .professional-image-upload__grid { grid-template-columns: repeat(3,minmax(0,1fr)); } }
</style>
